import { Component, OnInit, effect, inject, input } from '@angular/core';
import { PropertiesFilter } from '../../../../core/filters/properties.filter';
import { AddressTypes, PropertyStatuses } from '../../../../core/enums/data.enums';
import { FaDuotoneIconComponent } from "@fortawesome/angular-fontawesome";
import { ParentFocusDirective } from '../../../../core/directives/parent-focus.directive';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MultiSelectComponent } from "../../../../parts/multi-select/multi-select.component";
import { LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { PropertyPermission } from '../../../../core/enums/permissions.enums';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';

@Component({
  selector: 'ms-property-search-filter',
  imports: [CommonModule, FormsModule, FaDuotoneIconComponent, ParentFocusDirective, MultiSelectComponent],
  templateUrl: './property-search-filter.html'
})
export class PropertySearchFilter extends BaseSearchFilterComponent<PropertiesFilter> implements OnInit {
  public localFilter = input<PropertiesFilter | undefined>(undefined);
  public localStorageKey = input<string | undefined>(undefined);
  // Hide the Property Status dropdown — set true inside the Invoice Schedule property picker where
  // the ticket (DEV-1909) explicitly removes it, since billable Properties are always Active.
  public hideStatusFilter = input<boolean>(false);

  public addressTypes = AddressTypes;
  public selectPropertyStatusOptions: PropertyStatuses[] = [PropertyStatuses.Active, PropertyStatuses.Inactive];
  public canViewDeletedProperties = false;

  public titleInput = '';
  public lotNumberInput = '';
  public primaryAddressInput = '';
  public secondaryAddressInput = '';
  public cityInput = '';
  public stateProvinceInput = '';
  public addressTypeInput = '';
  public zipCodeInput = '';
  public countryInput = '';
  public minMemberCountInput = undefined;
  public maxMemberCountInput = undefined;

  public selectedPropertyStatus: PropertyStatuses[] = [];

  // Provided by an org-scoped profile page — this filter renders inside the property list on the
  // member profile (Properties tab, attach properties) — so read_deleted is gated on the
  // organization on screen rather than the active one. Null on the organization-wide Properties
  // page, which keeps the active organization (DEV-2643).
  private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  constructor(
    public userSessionService: UserSessionService,
    private authorizationService: AuthorizationService,
    private authorizationStateService: AuthorizationStateService
  ) {
    super();
    // Reactively re-evaluate deleted-property access when the auth state or the scoped organization
    // changes, so the option doesn't go stale once either finishes loading — and so failing closed on
    // a pending profile scope below doesn't leave the option stuck off after that scope resolves.
    // Mirrors member-search-filter, which has always done this (DEV-2643).
    effect(() => this.initializeDeletedPropertyAccess());
  }

  public override get activeFilter(): PropertiesFilter {
    return this.localFilter() ?? this.userSessionService.propertiesFilter;
  }

  protected override getDefaultFilter(): PropertiesFilter {
    return ListConstants.PROPERTIES_FILTER_DEFAULT();
  }

  protected override get preservedFilterKeys(): (keyof PropertiesFilter)[] {
    return ['organizationIds', 'memberIds'];
  }

  protected override get dropdownSelector(): string {
    return '#property-search-filter-dropdown';
  }

  protected override applyAuthGatedFields(): void {
    this.initializeDeletedPropertyAccess();
  }

  protected override syncStateFromFilter(): void {
    this.minMemberCountInput = undefined;
    this.maxMemberCountInput = undefined;
    this.selectedPropertyStatus = [...(this.activeFilter.statuses || [])];
  }

  public ngOnInit(): void {
    this.loadFromStorage();
    this.initializeDeletedPropertyAccess();
    this.selectedPropertyStatus = [...(this.activeFilter.statuses || [])];
    this.onApply();
  }

  private initializeDeletedPropertyAccess(): void {
    const authState = this.authorizationStateService.authorizationState();
    if (authState) {
      this.canViewDeletedProperties = PermissionUtilities.hasScopedOrganizationPermission(
        PropertyPermission.ReadDeleted, authState, this.authorizationService,
        this.profileScope, this.userSessionService
      );
    }

    if (this.canViewDeletedProperties) {
      this.selectPropertyStatusOptions = [PropertyStatuses.Active, PropertyStatuses.Inactive, PropertyStatuses.Deleted];
      this.activeFilter.includeDeletedProperties = true;
    } else {
      this.selectPropertyStatusOptions = [PropertyStatuses.Active, PropertyStatuses.Inactive];
      this.activeFilter.includeDeletedProperties = false;

      // Remove Deleted from any persisted filter statuses
      if (this.activeFilter.statuses?.includes(PropertyStatuses.Deleted)) {
        this.activeFilter.statuses = this.activeFilter.statuses.filter(s => s !== PropertyStatuses.Deleted);
        // The reactive re-evaluation above can now strip Deleted after the multi-select was already
        // populated, so the local mirror has to follow or the chip stays on screen unselectable.
        this.selectedPropertyStatus = [...this.activeFilter.statuses];
      }
    }
  }

  private loadFromStorage(): void {
    const storageKey = this.localStorageKey();
    if (storageKey && this.localFilter()) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const parsedFilter = JSON.parse(saved);
          const currentFilter = this.localFilter()!;
          const updatedFilter = { ...currentFilter, ...parsedFilter };
          Object.keys(updatedFilter).forEach(key => {
            (currentFilter as any)[key] = (updatedFilter as any)[key];
          });
        } catch (error) {
          console.error('Failed to load filter from localStorage:', error);
        }
      }
    }
  }

  protected override saveToStorage(): void {
    const storageKey = this.localStorageKey();

    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(this.activeFilter));
    } else if (!this.localFilter()) {
      this.userSessionService.saveToLocalStorage(
        LocalStorageKeyConstants.PROPERTIES_FILTER(),
        this.activeFilter
      );
    }
  }

  public override setFilterCount(): void {
    this.filterCount =
      (this.activeFilter.titles?.length || 0) +
      (this.activeFilter.lotNumbers?.length || 0) +
      (this.activeFilter.addressTypes?.length || 0) +
      (this.activeFilter.primaryAddresses?.length || 0) +
      (this.activeFilter.secondaryAddresses?.length || 0) +
      (this.activeFilter.cities?.length || 0) +
      (this.activeFilter.stateProvinces?.length || 0) +
      (this.activeFilter.primaryZips?.length || 0) +
      (this.activeFilter.countries?.length || 0) +
      (this.activeFilter.minimumMemberCount ? 1 : 0) +
      (this.activeFilter.maximumMemberCount ? 1 : 0) +
      (this.activeFilter.statuses?.length || 0);
  }

  public addLotNumber(): void {
    if (this.lotNumberInput && !(this.lotNumberInput.trim() === '')) {
      this.activeFilter.lotNumbers?.push(this.lotNumberInput);
      this.saveToStorage();
    }
    this.lotNumberInput = '';
  }

  public removeLotNumber(index: number): void {
    this.activeFilter.lotNumbers?.splice(index, 1);
    this.saveToStorage();
  }

  public addTitle(): void {
    if (this.titleInput && !(this.titleInput.trim() === '')) {
      this.activeFilter.titles?.push(this.titleInput);
      this.saveToStorage();
    }
    this.titleInput = '';
  }

  public removeTitle(index: number): void {
    this.activeFilter.titles?.splice(index, 1);
    this.saveToStorage();
  }

  public addPrimaryAddress(): void {
    if (this.primaryAddressInput && !(this.primaryAddressInput.trim() === '')) {
      this.activeFilter.primaryAddresses?.push(this.primaryAddressInput);
      this.saveToStorage();
    }
    this.primaryAddressInput = '';
  }

  public removePrimaryAddress(index: number): void {
    this.activeFilter.primaryAddresses?.splice(index, 1);
    this.saveToStorage();
  }

  public addSecondaryAddress(): void {
    if (this.secondaryAddressInput && !(this.secondaryAddressInput.trim() === '')) {
      this.activeFilter.secondaryAddresses?.push(this.secondaryAddressInput);
      this.saveToStorage();
    }
    this.secondaryAddressInput = '';
  }

  public removeSecondaryAddress(index: number): void {
    this.activeFilter.secondaryAddresses?.splice(index, 1);
    this.saveToStorage();
  }

  public addCity(): void {
    if (this.cityInput && !(this.cityInput.trim() === '')) {
      this.activeFilter.cities?.push(this.cityInput);
      this.saveToStorage();
    }
    this.cityInput = '';
  }

  public removeCity(index: number): void {
    this.activeFilter.cities?.splice(index, 1);
    this.saveToStorage();
  }

  public addStateProvince(): void {
    if (this.stateProvinceInput && !(this.stateProvinceInput.trim() === '')) {
      this.activeFilter.stateProvinces?.push(this.stateProvinceInput);
      this.saveToStorage();
    }
    this.stateProvinceInput = '';
  }

  public removeStateProvince(index: number): void {
    this.activeFilter.stateProvinces?.splice(index, 1);
    this.saveToStorage();
  }

  public addPrimaryZip(): void {
    if (this.zipCodeInput && !(this.zipCodeInput.trim() === '')) {
      this.activeFilter.primaryZips?.push(this.zipCodeInput);
      this.saveToStorage();
    }
    this.zipCodeInput = '';
  }

  public removePrimaryZip(index: number): void {
    this.activeFilter.primaryZips?.splice(index, 1);
    this.saveToStorage();
  }

  public addCountry(): void {
    if (this.countryInput && !(this.countryInput.trim() === '')) {
      this.activeFilter.countries?.push(this.countryInput);
      this.saveToStorage();
    }
    this.countryInput = '';
  }

  public removeCountry(index: number): void {
    this.activeFilter.countries?.splice(index, 1);
    this.saveToStorage();
  }

  public onStatusChange(event: any): void {
    this.activeFilter.statuses = event;
    this.saveToStorage();
  }

  public onMinMemberCountChange(): void {
    this.activeFilter.minimumMemberCount = (this.minMemberCountInput == "") ? undefined : this.minMemberCountInput;
    this.saveToStorage();
  }

  public onMaxMemberCountChange(): void {
    this.activeFilter.maximumMemberCount = (this.maxMemberCountInput == "") ? undefined : this.maxMemberCountInput;
    this.saveToStorage();
  }
}
